import React, { useState, useEffect } from 'react';
import { Sun, ExternalLink } from 'lucide-react';
import WeatherAnimations from './WeatherAnimations';
import WeatherWidget from './WeatherWidget';
import { useWeather } from '../hooks/useWeather';
import { weatherConfig } from '../config/sites';

const ExternalDashboard = ({ config }) => {
  const [iframeError, setIframeError] = useState(config.disableIframe || false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { weather, weatherTestMode } = useWeather();

  // Set page title
  useEffect(() => {
    document.title = `${config.name} - Solar Dashboard`;
  }, [config.name]);

  // Detect if iframe fails to load within 5 seconds (only if iframe not disabled)
  useEffect(() => {
    if (config.disableIframe) return;

    const timer = setTimeout(() => {
      if (!iframeLoaded) {
        setIframeError(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [iframeLoaded, config.disableIframe]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6 relative">
      {/* Weather Animations */}
      <WeatherAnimations weather={weather} weatherTestMode={weatherTestMode} />

      {/* Test Mode Indicator */}
      {weatherTestMode && (
        <div className="fixed bottom-4 left-4 bg-black/80 text-white px-4 py-2 rounded-lg z-50 text-sm">
          Test Mode: {weatherTestMode} (Press 'W' to cycle)
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-8 relative">
          {/* Weather Widget */}
          <WeatherWidget
            weather={weather}
            weatherTestMode={weatherTestMode}
            locationName={weatherConfig.location.name}
          />

          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent pb-1">
              {config.name}
            </h1>
          </div>
          <p className="text-gray-600 text-lg">{config.type === 'enphase' ? 'Enphase' : 'SolarEdge'} Solar Monitoring System</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className={`p-4 bg-gradient-to-r ${config.headerColor} flex items-center justify-between`}>
            <h2 className="text-xl font-semibold text-white">
              Live {config.type === 'enphase' ? 'Enphase' : 'SolarEdge'} Dashboard
            </h2>
            <a
              href={config.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 bg-white text-${config.buttonColor}-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium`}
            >
              Open in New Tab
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {!iframeError ? (
            <div className="relative" style={{ paddingBottom: '75%' }}>
              <iframe
                src={config.url}
                className="absolute top-0 left-0 w-full h-full"
                title={`${config.name} Solar Dashboard`}
                frameBorder="0"
                allowFullScreen
                onLoad={() => setIframeLoaded(true)}
                onError={() => setIframeError(true)}
              />
              {!iframeLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <div className="text-gray-500">Loading dashboard...</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="mb-6">
                <Sun className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Dashboard Embedded Display Restricted</h3>
                <p className="text-gray-600 mb-6">
                  The {config.type === 'enphase' ? 'Enphase' : 'SolarEdge'} dashboard cannot be displayed in an embedded frame due to security restrictions.
                </p>
              </div>
              <a
                href={config.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg shadow-lg"
              >
                View Dashboard in New Window
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          )}
        </div>

        <div className={`mt-6 bg-${config.buttonColor}-50 border border-${config.buttonColor}-200 rounded-lg p-4`}>
          <p className={`text-sm text-${config.buttonColor}-800`}>
            <strong>Note:</strong> This dashboard is powered by {config.type === 'enphase' ? "Enphase Energy's" : "SolarEdge's"} monitoring system.
            Data is provided directly from the {config.type === 'enphase' ? 'Enphase' : 'SolarEdge'} platform. If the embedded view doesn't load,
            click the "Open in New Tab" button above to view the full dashboard.
          </p>
        </div>
      </div>

      {/* Subtle credit in bottom right */}
      <div className="fixed bottom-2 right-2 text-xs text-gray-400 opacity-30 hover:opacity-70 transition-opacity pointer-events-none z-50">
        Designed and Built by Cory Fiala and Claude.AI
      </div>
    </div>
  );
};

export default ExternalDashboard;
